/**
 * Project / customer users for Assign drawer (same source as portal DPR assignments).
 * GET adminTools/listUsers/{customerId}
 */
import { transactionAxios } from './schedulingApis';
import { getCustomerId } from '../utils/cookies';
import { showErrorNotification } from '../utils/utils';
import type { AssignUserOption } from '../features/schedule-tasks/components/assignments/assignUsersTypes';

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value != null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['data', 'payload', 'result', 'users']) {
      if (Array.isArray(record[key])) return record[key] as unknown[];
    }
  }
  return [];
}

export function parseProjectUserOptions(raw: unknown): AssignUserOption[] {
  const out: AssignUserOption[] = [];
  const seen = new Set<string>();
  for (const row of asArray(raw)) {
    if (row == null || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const value = String(r.id ?? r.user_id ?? r.userId ?? '').trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    const label =
      String(r.name ?? r.fullName ?? r.displayName ?? r.userName ?? value).trim() ||
      value;
    out.push({ value, label });
  }
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

/** List users for the current customer (portal adminTools). */
export async function fetchProjectAssignUsers(options?: {
  customerId?: string;
  silent?: boolean;
}): Promise<AssignUserOption[]> {
  const customerId = String(options?.customerId ?? getCustomerId() ?? '').trim();
  if (!customerId) {
    if (!options?.silent) {
      showErrorNotification(
        new Error('Missing customer id'),
        'Sorry! Cannot load users without a customer.',
      );
    }
    return [];
  }

  try {
    const res = await transactionAxios.get(
      `adminTools/listUsers/${encodeURIComponent(customerId)}`,
    );
    return parseProjectUserOptions(res.data?.data ?? res.data);
  } catch (err) {
    console.error(`Error fetching assign users: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to fetch users.`);
    }
    throw err;
  }
}
